import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"

import { cn } from "../../lib/utils"
import { Loading } from "../../icons/loading"

const buttonVariants = cva(
   "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-(--borderRadius) text-sm font-bold leading-normal tracking-[0.015em] transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
   {
      variants: {
         variant: {
            default: "bg-(--primaryColor) text-white! hover:bg-(--primaryColor)/90",
            destructive:
               "bg-destructive text-white hover:!bg-destructive/70 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/80",
            outline:
               "text-gray-900 dark:text-white focus:outline-none focus:ring-2! focus:ring-primary! focus:ring-opacity-50! border border-border-light dark:border-border-dark active:border-primary! bg-background-light dark:bg-background-dark h-11",
            secondary:
               "bg-secondary text-white hover:bg-secondary/80",
            ghost:
               "hover:bg-accent text-(--textColor)! dark:hover:bg-accent/50",
            link:
               "text-primary underline-offset-4 hover:underline",
         },

         isLoading: {
            false: "duration-150",
            true: "[&>*:not(.loading-icon)]:opacity-0 text-transparent duration-200",
         },

         size: {
            default: "h-10 px-5 py-3 has-[>svg]:px-4",
            sm: "h-8 rounded-(--borderRadius) gap-1.5 text-sm px-3 has-[>svg]:px-2.5",
            md: "h-9.5 rounded-(--borderRadius) gap-1.5 text-sm px-4 has-[>svg]:px-3.5",
            lg: "h-14 rounded-(--borderRadius) px-6",
            icon: "size-9",
            "icon-sm": "size-8",
            "icon-lg": "size-10",
         },
      },

      defaultVariants: {
         variant: "default",
         size: "default",
      },
   }
)

const Button = React.forwardRef(
   (
      {
         className,
         children,
         variant,
         size,
         asChild = false,
         isLoading = false,
         ...props
      },
      ref
   ) => {
      const Comp = asChild ? Slot : "button"

      return (
         <Comp
            ref={ref}
            data-slot="button"
            className={cn(
               buttonVariants({
                  variant,
                  size,
                  className,
                  isLoading,
               })
            )}
            {...props}
         >
            {children}

            {isLoading && (
               <Loading className="loading-icon absolute left-1/2 top-1/2 -translate-1/2" />
            )}
         </Comp>
      )
   }
)

Button.displayName = "Button"

export { Button, buttonVariants }
